import { Link, useNavigate } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { useState } from "react";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";

export default function Cart() {
  const { items, update, remove, total } = useCart();
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  const checkout = async () => {
    if (!user) { navigate("/login"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/checkout/session", {
        items: items.map(i => ({ product_id: i.id, quantity: i.quantity })),
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Checkout failed");
      setBusy(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-6 py-12" data-testid="cart-page">
      <div className="label-eyebrow">Your bag</div>
      <h1 className="mt-2 font-display text-4xl text-[#0A192F]">Shopping cart</h1>

      {items.length === 0 ? (
        <div className="mt-12 card-flat p-12 text-center">
          <div className="text-slate-500">Your cart is empty.</div>
          <Link to="/store"><Button className="mt-6 bg-[#002855] hover:bg-[#0A192F] text-white rounded-md">Browse the shop</Button></Link>
        </div>
      ) : (
        <div className="mt-8 grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-3">
            {items.map(i => (
              <div key={i.id} className="card-flat p-4 flex gap-4 items-center" data-testid={`cart-item-${i.id}`}>
                <img src={i.image_url} alt={i.name} className="w-20 h-20 object-cover rounded" />
                <div className="flex-1">
                  <div className="font-display text-lg text-[#0A192F]">{i.name}</div>
                  <div className="text-sm text-slate-500 font-mono">${i.price.toFixed(2)}</div>
                </div>
                <div className="flex items-center border rounded-md">
                  <button onClick={() => update(i.id, i.quantity - 1)} className="px-2.5 h-9">−</button>
                  <div className="w-8 text-center font-mono text-sm">{i.quantity}</div>
                  <button onClick={() => update(i.id, i.quantity + 1)} className="px-2.5 h-9">+</button>
                </div>
                <div className="w-20 text-right font-mono text-[#0A192F]">${(i.price * i.quantity).toFixed(2)}</div>
                <button onClick={() => remove(i.id)} className="text-slate-400 hover:text-[#9B111E]" data-testid={`remove-${i.id}`}><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
          <aside className="card-flat p-6 h-fit">
            <div className="label-eyebrow">Order summary</div>
            <dl className="mt-4 space-y-2 text-sm">
              <div className="flex justify-between"><dt className="text-slate-500">Subtotal</dt><dd className="font-mono text-[#0A192F]">${total.toFixed(2)}</dd></div>
              <div className="flex justify-between"><dt className="text-slate-500">Shipping</dt><dd className="text-slate-500">Calculated at checkout</dd></div>
            </dl>
            <div className="border-t mt-4 pt-4 flex justify-between font-display text-xl text-[#0A192F]">
              <span>Total</span>
              <span data-testid="cart-total">${total.toFixed(2)}</span>
            </div>
            <Button onClick={checkout} disabled={busy} className="w-full mt-6 bg-[#002855] hover:bg-[#0A192F] text-white rounded-md h-12" data-testid="checkout-button">
              {busy ? "Redirecting…" : user ? "Checkout with Stripe" : "Sign in to checkout"}
            </Button>
            <Link to="/store"><Button variant="outline" className="w-full mt-3 border-slate-200 rounded-md">Continue shopping</Button></Link>
          </aside>
        </div>
      )}
    </div>
  );
}
